import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Tooltip from "@mui/material/Tooltip";
import { LINKEDIN, META, TIKTOK } from "../../../utils/constants";
import {
  CREATED_AT,
  TYPE_PLACEHOLDER,
  CAMPAIGNS_NOT_FOUND,
  DATE_SHORT_2,
  GOOGLE,
  OBJECTIVE_PLACEHOLDER,
  STATUS_PLACEHOLDER,
  ALREADY_IMPORTED,
} from "../../../i18n/keysTranslations";
import { useTranslationApp } from "../../../lib/i18next";
import {
  optionsAdsCampaignObjectivesLinkedin,
  optionsAdsCampaignObjectivesMeta,
  optionsAdsCampaignObjectivesTikTok,
  optionsAdsCampaignStatus,
  optionsCampaignsTypeGoogleAds,
} from "../../../utils/options";
import Loader from "../../Loaders/Loader";
import { Chip } from "@mui/material";
import { INPUT_COLOR_BORDER } from "../../../utils/colors";

const sxCutText = (maxLines = 2) => ({
  whiteSpace: "normal",
  overflow: "hidden",
  textOverflow: "ellipsis",
  display: "-webkit-box",
  WebkitLineClamp: maxLines,
  WebkitBoxOrient: "vertical",
});

const getObjectiveLabelTranslation = (platform) => {
  switch (platform) {
    case GOOGLE:
      return TYPE_PLACEHOLDER;

    default:
      return OBJECTIVE_PLACEHOLDER;
  }
};
const getObjectiveValueTranslation = (platform, objective) => {
  let objectiveTranslation;
  switch (platform) {
    case GOOGLE:
      objectiveTranslation =
        optionsCampaignsTypeGoogleAds.find(
          (campaignType) => campaignType.value === objective
        )?.label || objective;

      return objectiveTranslation;

    case META:
      objectiveTranslation =
        optionsAdsCampaignObjectivesMeta.find(
          (campaignType) => campaignType.value === objective
        )?.label || objective;
      return objectiveTranslation;

    case TIKTOK:
      objectiveTranslation =
        optionsAdsCampaignObjectivesTikTok.find(
          (campaignType) => campaignType.value === objective
        )?.label || objective;
      return objectiveTranslation;

    case LINKEDIN:
      objectiveTranslation =
        optionsAdsCampaignObjectivesLinkedin.find(
          (campaignType) => campaignType.value === objective
        )?.label || objective;
      return objectiveTranslation;
    default:
      return objective;
  }
};

const ImportAdsCampaignList = ({
  data,
  platform,
  lastItemRef,
  loadedLastCampaign,
  selectedCampaign,
  onSelectCampaign,
  ignoreAlreadyCreated = false,
}) => {
  const { t } = useTranslationApp();

  if (data.length === 0 && loadedLastCampaign) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          color: "background.paperContrastText",
        }}
      >
        {t(CAMPAIGNS_NOT_FOUND)}
      </Box>
    );
  }

  if (data.length === 0 && !loadedLastCampaign) {
    return (
      <Box ref={lastItemRef}>
        <Loader size={20} hasMarginTop={true} />
      </Box>
    );
  }

  const isBlockedCampaign = (campaign) =>
    !ignoreAlreadyCreated && campaign.isAlreadyCreated;

  const getColorText = (campaign) => {
    if (selectedCampaign === campaign.id) {
      return "white !important";
    }
    return "black !important";
  };

  return (
    <Grid container spacing={2}>
      {data.map((campaign, index) => (
        <>
          <Grid item xs={12} md={6} key={campaign.id}>
            <Box
              onClick={() => {
                if (isBlockedCampaign(campaign)) return;
                onSelectCampaign(campaign);
              }}
              sx={{
                cursor: isBlockedCampaign(campaign) ? "default" : "pointer",
                backgroundColor:
                  selectedCampaign === campaign.id
                    ? "#1a1a1a !important"
                    : "white !important",
                borderRadius: "10px",
                border: `1px solid ${INPUT_COLOR_BORDER}`,
                boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
                py: { xs: 2, sm: 2 },
                px: { xs: 2, sm: 3 },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  width: "100%",
                  gap: 1,
                }}
              >
                <Tooltip title={campaign.name}>
                  <Typography
                    variant="body1"
                    sx={{
                      color: getColorText(campaign),
                      ...sxCutText(1),
                    }}
                  >
                    {campaign.name}
                  </Typography>
                </Tooltip>
                {isBlockedCampaign(campaign) && (
                  <Chip
                    label={t(ALREADY_IMPORTED)}
                    size="small"
                    sx={{
                      color: "background.paperContrastText",
                      backgroundColor: "#E5E5E5",
                    }}
                  />
                )}
              </Box>
              <Box
                sx={{
                  mt: 1,
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr",
                  columnGap: 2,
                  "& .MuiTypography-caption, & .MuiTypography-body2": {
                    fontSize: "0.7rem",
                  },
                }}
              >
                <Box
                  sx={{
                    minWidth: 0,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-end",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      whiteSpace: "nowrap",
                      color: getColorText(campaign),
                    }}
                  >
                    {t(getObjectiveLabelTranslation(platform))}
                  </Typography>
                  <Tooltip
                    title={t(
                      getObjectiveValueTranslation(platform, campaign.objective)
                    )}
                  >
                    <Typography
                      variant="body2"
                      sx={sxCutText(1)}
                      color={getColorText(campaign)}
                    >
                      {t(
                        getObjectiveValueTranslation(
                          platform,
                          campaign.objective
                        )
                      )}
                    </Typography>
                  </Tooltip>
                </Box>
                {campaign.creationTime && (
                  <Box
                    sx={{
                      minWidth: 0,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "flex-end",
                    }}
                  >
                    <Typography
                      variant="caption"
                      color={getColorText(campaign)}
                    >
                      {t(CREATED_AT)}
                    </Typography>
                    <Tooltip
                      title={t(DATE_SHORT_2, {
                        date: new Date(campaign.creationTime),
                      })}
                    >
                      <Typography
                        variant="body2"
                        sx={sxCutText(1)}
                        color={getColorText(campaign)}
                      >
                        {t(DATE_SHORT_2, {
                          date: new Date(campaign.creationTime),
                        })}
                      </Typography>
                    </Tooltip>
                  </Box>
                )}
                <Box
                  sx={{
                    minWidth: 0,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-end",
                  }}
                >
                  <Typography variant="caption" color={getColorText(campaign)}>
                    {t(STATUS_PLACEHOLDER)}
                  </Typography>
                  <Tooltip
                    title={t(
                      optionsAdsCampaignStatus.find(
                        (status) => status.value === campaign.status
                      )?.label
                    )}
                  >
                    <Typography
                      variant="body2"
                      sx={sxCutText(1)}
                      color={getColorText(campaign)}
                    >
                      {t(
                        optionsAdsCampaignStatus.find(
                          (status) => status.value === campaign.status
                        )?.label
                      )}
                    </Typography>
                  </Tooltip>
                </Box>
              </Box>
            </Box>
          </Grid>
          {index === data.length - 1 && !loadedLastCampaign && (
            <Grid item xs={12}>
              <Box ref={lastItemRef} />
              <Loader size={20} hasMarginTop={true} />
            </Grid>
          )}
        </>
      ))}
    </Grid>
  );
};

export default ImportAdsCampaignList;
